import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import {
  Archive,
  BookOpen,
  ChevronRight,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  Search,
  ShieldCheck,
  UserRound,
  UserRoundCog,
  UsersRound,
  X
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';
import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';
import { PERMISSIONS } from '../../core/permissions/roles';
import AccountManagementPanel from '../dashboard/AccountManagementPanel';
import { classNameFromId, studentClassId, useSchoolClasses } from '../../core/school/classes';


function text(value) {
  return String(value ?? '').trim();
}

function studentName(student) {
  return text(student.fullName || student.fullname || [student.firstName, student.lastName].filter(Boolean).join(' ')) || 'Élève sans nom';
}

function classValue(student, classes) {
  const classId = studentClassId(student, classes);
  return classNameFromId(classId, classes, text(student.classLabel || student.className || student.level || student.classId) || 'Classe non affectée');
}

function normalizeLevel(value) {
  return text(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
}

function initials(name) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'EL';
}

function dateLabel(timestamp) {
  if (!timestamp?.toDate) return 'Date non renseignée';
  return timestamp.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}


function profileRoles(profile) {
  const raw = profile?.adminRoles ?? profile?.adminRole ?? profile?.role ?? [];
  return (Array.isArray(raw) ? raw : [raw])
    .filter(Boolean)
    .map((role) =>
      role === 'admin' || role === 'administration' ? 'gerant' : role
    );
}

function editableProfile(student, classes) {
  return {
    fullName: studentName(student),
    classId: studentClassId(student, classes),
    parentName: text(student.parentName),
    parentPhone: text(student.parentPhone),
    parentEmail: text(student.parentEmail),
    address: text(student.address),
    notes: text(student.adminNotes || student.notesText)
  };
}

export default function StudentsPage() {
  const { can, profile } = useAuth();
  const { classes } = useSchoolClasses();
  const canManage = can(PERMISSIONS.STUDENTS_MANAGE);
  const canManageAccounts = profileRoles(profile).includes('gerant');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [queryText, setQueryText] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [accountsOpen, setAccountsOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'students'), (snapshot) => {
      const rows = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      rows.sort((a, b) => studentName(a).localeCompare(studentName(b), 'fr'));
      setStudents(rows);
      setLoading(false);
      setError('');
    }, (loadError) => {
      console.error(loadError);
      setError('Impossible de charger les élèves. Vérifie les autorisations d’accès.');
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const selected = students.find((student) => student.id === selectedId) || null;

  useEffect(() => {
    if (selected) setForm(editableProfile(selected, classes));
  }, [selectedId, selected, classes]);

  const stats = useMemo(() => {
    const active = students.filter((student) => student.status !== 'archived');
    const archived = students.filter((student) => student.status === 'archived');
    const withoutClass = active.filter((student) => !studentClassId(student, classes));
    const parents = new Set(active.flatMap((student) => [student.parentUid, ...(Array.isArray(student.parentUids) ? student.parentUids : [])]).filter(Boolean));
    return { total: students.length, active: active.length, archived: archived.length, withoutClass: withoutClass.length, parents: parents.size };
  }, [students, classes]);

  const visibleStudents = useMemo(() => {
    const term = queryText.trim().toLowerCase();
    return students.filter((student) => {
      const status = student.status || 'active';
      if (statusFilter === 'active' && status === 'archived') return false;
      if (statusFilter === 'archived' && status !== 'archived') return false;
      if (levelFilter !== 'all' && studentClassId(student, classes) !== levelFilter) return false;
      if (!term) return true;
      const haystack = [
        studentName(student),
        classValue(student, classes),
        student.parentName,
        student.parentPhone,
        student.parentEmail,
        student.registrationId,
        student.id
      ].map(text).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [students, queryText, levelFilter, statusFilter, classes]);

  function openStudent(id) {
    setSelectedId(id);
    setEditing(false);
    setMessage('');
  }

  async function saveStudent(event) {
    event.preventDefault();
    if (!selected || !canManage) return;
    setSaving(true);
    setMessage('');
    try {
      await updateDoc(doc(db, 'students', selected.id), {
        fullName: form.fullName.trim(),
        fullname: form.fullName.trim(),
        classId: form.classId || '',
        className: classNameFromId(form.classId, classes, ''),
        classLabel: classNameFromId(form.classId, classes, ''),
        level: classNameFromId(form.classId, classes, ''),
        parentName: form.parentName.trim(),
        parentPhone: form.parentPhone.trim(),
        parentEmail: form.parentEmail.trim(),
        address: form.address.trim(),
        adminNotes: form.notes.trim(),
        updatedAt: serverTimestamp(),
        updatedByName: profile?.fullname || 'Administration'
      });
      setEditing(false);
      setMessage('Dossier mis à jour.');
    } catch (saveError) {
      console.error(saveError);
      setMessage('La modification n’a pas pu être enregistrée.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleArchive() {
    if (!selected || !canManage) return;
    const archived = selected.status === 'archived';
    if (!window.confirm(archived ? 'Réactiver cet élève ?' : 'Archiver cet élève ? Son dossier restera conservé.')) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'students', selected.id), {
        status: archived ? 'active' : 'archived',
        archivedAt: archived ? null : serverTimestamp(),
        archivedByName: archived ? null : (profile?.fullname || 'Administration'),
        updatedAt: serverTimestamp()
      });
      setMessage(archived ? 'Élève réactivé.' : 'Élève archivé.');
    } catch (archiveError) {
      console.error(archiveError);
      setMessage('Cette action n’a pas pu être effectuée.');
    } finally {
      setSaving(false);
    }
  }

  return <>
    <PageHeader eyebrow="MODULE" title="Élèves" description="Consultez, recherchez et gérez les dossiers des élèves."/>

    {error && <div className="dashboard-alert"><div><strong>Une erreur est survenue.</strong><span>{error}</span></div></div>}

    <section className="stats-grid student-stats">
      <StatCard label="Élèves actifs" value={loading ? '…' : stats.active} hint="Dossiers en cours" tone="blue"/>
      <StatCard label="Sans classe" value={loading ? '…' : stats.withoutClass} hint="À affecter" tone="amber"/>
      <StatCard label="Parents liés" value={loading ? '…' : stats.parents} hint="Comptes associés" tone="green"/>
      <StatCard label="Archives" value={loading ? '…' : stats.archived} hint="Dossiers conservés" tone="rose"/>
    </section>

    {canManageAccounts && (
      <section className="student-account-access-card panel">
        <div className="student-account-access-copy">
          <span className="student-account-access-icon">
            <UserRoundCog size={23}/>
          </span>
          <div>
            <p className="eyebrow">GÉRANCE</p>
            <h2>Gestion des comptes</h2>
            <p>
              Désactivez un accès ou supprimez les données d’un compte de test.
              La liste reste masquée tant que cet espace n’est pas ouvert.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="button button-secondary"
          onClick={() => setAccountsOpen(true)}
        >
          <UserRoundCog size={17}/>
          Ouvrir la gestion des comptes
        </button>
      </section>
    )}

    <section className="student-toolbar panel">
      <label className="student-search"><Search size={18}/><input value={queryText} onChange={(event) => setQueryText(event.target.value)} placeholder="Rechercher par élève, parent, téléphone ou classe…"/></label>
      <select value={levelFilter} onChange={(event) => setLevelFilter(event.target.value)}>
        <option value="all">Toutes les classes</option>{classes.map((schoolClass) => <option key={schoolClass.id} value={schoolClass.id}>{schoolClass.name || schoolClass.id}</option>)}
      </select>
      <div className="segmented-control">
        <button className={statusFilter === 'active' ? 'active' : ''} onClick={() => setStatusFilter('active')}>Actifs</button>
        <button className={statusFilter === 'archived' ? 'active' : ''} onClick={() => setStatusFilter('archived')}>Archivés</button>
        <button className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>Tous</button>
      </div>
    </section>

    <section className="panel student-panel">
      <div className="panel-heading">
        <div><p className="eyebrow">DOSSIERS</p><h2>{visibleStudents.length} élève{visibleStudents.length > 1 ? 's' : ''}</h2></div>
        <span className="live-badge"><span/>Mise à jour automatique</span>
      </div>

      {loading ? <div className="compact-empty">Chargement des élèves…</div> : visibleStudents.length === 0 ? <div className="compact-empty"><GraduationCap size={28}/><strong>Aucun élève trouvé.</strong><span>Modifie la recherche ou les filtres.</span></div> : <div className="student-list">
        {visibleStudents.map((student) => {
          const name = studentName(student);
          return <button className="student-row" key={student.id} onClick={() => openStudent(student.id)}>
            <span className="student-avatar">{initials(name)}</span>
            <span className="student-main"><strong>{name}</strong><small>{classValue(student, classes)}</small></span>
            <span className="student-parent"><UsersRound size={15}/>{text(student.parentName) || 'Parent non renseigné'}</span>
            <span className={`student-status ${student.status === 'archived' ? 'archived' : 'active'}`}>{student.status === 'archived' ? 'Archivé' : 'Actif'}</span>
            <ChevronRight size={18}/>
          </button>;
        })}
      </div>}
    </section>



    {accountsOpen && canManageAccounts && (
      <div
        className="modal-layer student-account-modal-layer"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setAccountsOpen(false);
        }}
      >
        <section className="student-account-modal">
          <header className="student-account-modal-header">
            <div>
              <p className="eyebrow">GÉRANCE</p>
              <h2>Gestion des comptes</h2>
            </div>
            <button
              type="button"
              className="drawer-close"
              onClick={() => setAccountsOpen(false)}
              aria-label="Fermer"
            >
              <X size={20}/>
            </button>
          </header>
          <div className="student-account-modal-body">
            <AccountManagementPanel />
          </div>
        </section>
      </div>
    )}


        {selected && <div className="drawer-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setSelectedId(null)}>
      <aside className="registration-drawer student-drawer">
        <header className="drawer-header">
          <div><p className="eyebrow">DOSSIER ÉLÈVE</p><h2>{studentName(selected)}</h2><span className={`student-status ${selected.status === 'archived' ? 'archived' : 'active'}`}>{selected.status === 'archived' ? 'Archivé' : 'Actif'}</span></div>
          <button className="drawer-close" onClick={() => setSelectedId(null)} aria-label="Fermer"><X size={20}/></button>
        </header>
        <div className="drawer-scroll">
          {message && <div className="success-note"><ShieldCheck size={18}/>{message}</div>}

          {!editing ? <>
            <section className="detail-card student-hero-card">
              <div className="student-profile-avatar">{initials(studentName(selected))}</div>
              <div><h3>{studentName(selected)}</h3><p>{classValue(selected, classes)}</p><small>Dossier créé le {dateLabel(selected.createdAt)}</small></div>
            </section>

            <section className="detail-card"><h3><UserRound size={18}/>Informations</h3><dl className="detail-grid">
              <div><dt>Classe / niveau</dt><dd>{classValue(selected, classes)}</dd></div>
              <div><dt>Année scolaire</dt><dd>{text(selected.schoolYear) || 'Non renseignée'}</dd></div>
              <div><dt>Statut</dt><dd>{selected.status === 'archived' ? 'Archivé' : 'Actif'}</dd></div>
              <div><dt>Référence</dt><dd>{selected.id}</dd></div>
            </dl></section>

            <section className="detail-card"><h3><UsersRound size={18}/>Responsable</h3><div className="contact-stack">
              <div><UserRound size={16}/><span>{text(selected.parentName) || 'Nom non renseigné'}</span></div>
              <div><Phone size={16}/><span>{text(selected.parentPhone) || 'Téléphone non renseigné'}</span></div>
              <div><Mail size={16}/><span>{text(selected.parentEmail) || 'Email non renseigné'}</span></div>
              <div><MapPin size={16}/><span>{text(selected.address) || 'Adresse non renseignée'}</span></div>
            </div></section>

            <section className="detail-card"><h3><BookOpen size={18}/>Notes administratives</h3><p className="student-notes">{text(selected.adminNotes || selected.notesText) || 'Aucune note enregistrée.'}</p></section>

            {canManage && <div className="student-drawer-actions">
              <button className="button button-primary" onClick={() => setEditing(true)}>Modifier le dossier</button>
              <button className="button button-secondary" disabled={saving} onClick={toggleArchive}><Archive size={17}/>{selected.status === 'archived' ? 'Réactiver' : 'Archiver'}</button>
            </div>}
          </> : <form className="detail-card student-edit-form" onSubmit={saveStudent}>
            <h3>Modifier le dossier</h3>
            <label>Nom complet<input value={form.fullName || ''} onChange={(event) => setForm({ ...form, fullName: event.target.value })} required/></label>
            <label>Classe / niveau<select value={form.classId || ''} onChange={(event) => setForm({ ...form, classId: event.target.value })}><option value="">Classe non affectée</option>{classes.map((schoolClass) => <option key={schoolClass.id} value={schoolClass.id}>{schoolClass.name || schoolClass.id}</option>)}</select></label>
            <label>Responsable légal<input value={form.parentName || ''} onChange={(event) => setForm({ ...form, parentName: event.target.value })}/></label>
            <div className="form-two-columns"><label>Téléphone<input value={form.parentPhone || ''} onChange={(event) => setForm({ ...form, parentPhone: event.target.value })}/></label><label>Email<input type="email" value={form.parentEmail || ''} onChange={(event) => setForm({ ...form, parentEmail: event.target.value })}/></label></div>
            <label>Adresse<input value={form.address || ''} onChange={(event) => setForm({ ...form, address: event.target.value })}/></label>
            <label>Notes<textarea value={form.notes || ''} onChange={(event) => setForm({ ...form, notes: event.target.value })}/></label>
            <div className="student-drawer-actions"><button className="button button-primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button><button type="button" className="button button-secondary" onClick={() => setEditing(false)}>Annuler</button></div>
          </form>}
        </div>
      </aside>
    </div>}
  </>;
}
